// =============================================================================
// src/auth/identityOverrides.js
// Per-identity interface overrides kept in sessionStorage:
//   - "View as role": a staff member previews another role in their practice
//   - Spectator: a super admin views another practice as its Owner
//
// Both change what useAuth() reports as role / practiceId, so they belong to
// the person who set them and to nobody else. They used to be stored as bare
// values, which survived an identity change in the same tab: a session ending
// externally (expiry, sign-out in another tab) or a different account signing
// in from another tab (supabase-js syncs the session across tabs). The next
// account - a patient, in the Oct 2026 incident - then got the staff shell,
// and every staff RPC it called failed with 42501 (permission denied).
//
// Every entry is now stamped with the uid that set it, and is honored only for
// that uid. A role preview applies only to an Owner or a super admin, and
// never to a Patient or System account.
// Pure functions: the store is passed in, so the contract test runs in node.
// =============================================================================

export const VIEW_AS_KEY = "practiceos.viewAsRole";
export const SPECTATOR_KEY = "practiceos.spectator";

// Accounts that never get the staff shell through a preview.
const NO_PREVIEW_ROLES = new Set(["Patient", "System"]);

function safeGet(store, key) {
  try { return store ? store.getItem(key) : null; } catch { return null; }
}
function safeParse(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

// { uid, role } or null. A legacy bare-string value has no owner: ignored.
export function readViewAs(store) {
  const v = safeParse(safeGet(store, VIEW_AS_KEY));
  return v && typeof v === "object" && v.uid && v.role ? { uid: v.uid, role: v.role } : null;
}

// Spectator snapshot or null. One without an owner uid is ignored.
export function readSpectator(store) {
  const v = safeParse(safeGet(store, SPECTATOR_KEY));
  return v && typeof v === "object" && v.uid && v.practice_id ? v : null;
}

export function writeViewAs(store, uid, role) {
  try {
    if (uid && role) store.setItem(VIEW_AS_KEY, JSON.stringify({ uid, role }));
    else store.removeItem(VIEW_AS_KEY);
  } catch { /* private mode - the preview still applies in memory */ }
}

// Store a spectator snapshot (it carries its owner uid), or remove it.
export function writeSpectator(store, snapshot) {
  try {
    if (snapshot && snapshot.uid) store.setItem(SPECTATOR_KEY, JSON.stringify(snapshot));
    else store.removeItem(SPECTATOR_KEY);
  } catch { /* private mode - spectator still applies in memory */ }
}

// Only an Owner or a super admin can pick a role preview, so only they get one.
function canPreview(realRole, isSuperAdmin) {
  if (!realRole || NO_PREVIEW_ROLES.has(realRole)) return false;
  return realRole === "Owner" || Boolean(isSuperAdmin);
}

// The overrides that apply to the signed-in account right now.
export function effectiveOverrides({ uid, realRole, isSuperAdmin = false, viewAs, spectator }) {
  const owns = (entry) => Boolean(uid && entry && entry.uid === uid);
  return {
    viewAsRole: owns(viewAs) && canPreview(realRole, isSuperAdmin) ? viewAs.role : null,
    spectator: owns(spectator) ? spectator : null,
  };
}

// Remove stored overrides that do not belong to uid (all of them when uid is
// null, i.e. signed out). Returns true when anything was removed.
export function clearForeignOverrides(store, uid) {
  let removed = false;
  for (const [key, read] of [[VIEW_AS_KEY, readViewAs], [SPECTATOR_KEY, readSpectator]]) {
    const raw = safeGet(store, key);
    if (raw == null) continue;
    const entry = read(store);
    if (!uid || !entry || entry.uid !== uid) {
      try { store.removeItem(key); removed = true; } catch { /* ignore */ }
    }
  }
  return removed;
}
